
import { metricDesc } from "@/lib/metric-descriptions";
import { getPostTypeLabel } from "@/lib/post-type-labels";
import type { FBPost, IGPost } from "@/lib/types";

interface PayloadEntry {
  name?: string;
  value?: number;
  color?: string;
  dataKey?: string;
}

interface ChartTooltipProps {
  active?: boolean;
  payload?: PayloadEntry[];
  label?: string;
  isDate?: boolean;
  fbPosts?: FBPost[];
  igPosts?: IGPost[];
}

function fmt(n: number) {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return n.toLocaleString("cs-CZ");
}

export function ChartTooltip({ active, payload, label, isDate = true, fbPosts, igPosts }: ChartTooltipProps) {
  if (!active || !payload?.length) return null;

  const formattedLabel = isDate && label
    ? (() => { const d = new Date(label); return `${d.getDate()}.${d.getMonth() + 1}.${d.getFullYear()}`; })()
    : label;

  // Find posts for this date
  const dayFB = isDate && fbPosts ? fbPosts.filter(p => p.date === label && p.post_id) : [];
  const dayIG = isDate && igPosts ? igPosts.filter(p => p.date === label && p.media_id) : [];
  const hasPosts = dayFB.length > 0 || dayIG.length > 0;

  return (
    <div className="rounded-lg border border-border bg-[#1a1a1a] px-4 py-3 shadow-xl shadow-black/50 max-w-sm">
      <p className="text-sm font-semibold text-foreground mb-2">{formattedLabel}</p>

      {/* Metric values */}
      {payload.map((entry, i) => {
        const name = entry.name || "Hodnota";
        const desc = metricDesc[name];
        return (
          <div key={i} className="mb-1.5">
            <div className="flex items-center gap-2">
              <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: entry.color }} />
              <span className="text-sm text-muted-foreground">{name}</span>
              <span className="text-sm font-bold text-foreground ml-auto">{fmt(entry.value ?? 0)}</span>
            </div>
            {desc && <p className="text-[11px] text-muted-foreground/70 ml-[18px] mt-0.5">{desc}</p>}
          </div>
        );
      })}

      {/* Posts on this day */}
      {hasPosts && (
        <div className="mt-2 pt-2 border-t border-border space-y-1">
          {dayFB.map((p, i) => (
            <div key={`fb-${i}`} className="flex items-center gap-2">
              <span className="text-[10px] font-bold text-[#c9a84c] bg-[#c9a84c]/10 px-1 rounded">FB</span>
              <span className="text-xs text-foreground">{p.account_name}</span>
              {p.type && <span className="text-[10px] text-muted-foreground">{getPostTypeLabel(p.type)}</span>}
            </div>
          ))}
          {dayIG.map((p, i) => (
            <div key={`ig-${i}`} className="flex items-center gap-2">
              <span className="text-[10px] font-bold text-[#d4816b] bg-[#d4816b]/10 px-1 rounded">IG</span>
              <span className="text-xs text-foreground">{p.account_name}</span>
              <span className="text-[10px] text-muted-foreground">{getPostTypeLabel(p.media_product_type || p.media_type)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
